import { detectIncompatibleBrowsers } from 'helpers/common';
import * as React from 'react';

// make these props if needed
const MOUSE_HOLD_TIMEOUT = 300;
const DRAG_TARGET_CLASS = 'drag-target';
const TOUCH_HOLD_TIMEOUT = 300;
const HOLD_DISTANCE = 25;

interface IProps {
	/**
	 * x and y coords of the draggable element coordinate won't be smaller than bottomCoordLimit
	 */
	bottomCoordLimit?: { x: number; y: number };
	style?: React.CSSProperties;
	className?: string;
	title?: string;
	onClick?: any;
	onMouseEnter?: any;
	onMouseLeave?: any;
	/*
	if disabled is true, the item can not be dragged
	*/
	disabled?: boolean;

	onDragStart: () => void;
	onDragEnd: (targetElement: HTMLElement) => void;
	onTargetChanged: (targetElement: HTMLElement) => any;
}

const DraggableEvent: React.FC<IProps> = (props) => {
	const outerRef = React.useRef<HTMLDivElement>();
	const innerRef = React.useRef<HTMLDivElement>();
	const holdTimer = React.useRef(null);
	// scroll function is generated, so it has to be saved for removal
	const scrollFncRef = React.useRef(null);

	const [isDragging, _setIsDragging] = React.useState(false);
	//
	const isDraggingRef = React.useRef(false);

	const setIsDragging = (val) => {
		isDraggingRef.current = val;
		_setIsDragging(val);
	};
	// location of inner div in the local coordinate system of outer div
	const [offset, setOffset] = React.useState({ x: 0, y: 0 });
	// location of the curser, relative to scroll position
	const [mousePos, setMousePos] = React.useState({ x: 0, y: 0 });
	// location of inner div in the local coordinate system of the mouse
	const [mouseOffset, setMouseOffset] = React.useState({ x: 0, y: 0 });
	// used only to update when scroll is changed
	const [scroll, setScroll] = React.useState(0);
	const targetElement = React.useRef(null);

	const { onMouseEnter, onMouseLeave } = props;

	// notes:
	// event listeners have no access to hooks, so useEffect is used
	//

	const updateOffset = () => {
		const xLim = props.bottomCoordLimit?.x - window.scrollX || -Infinity;
		const yLim = props.bottomCoordLimit?.y - window.scrollY || -Infinity;

		if (isDragging) {
			const outerDivRect = outerRef.current?.getBoundingClientRect();
			setOffset({
				x: Math.max(xLim, mousePos?.x - mouseOffset?.x) - outerDivRect?.x,
				y: Math.max(yLim, mousePos?.y - mouseOffset?.y) - outerDivRect?.y,
			});
		}
	};
	// update element position (offset) when mouse position changes
	React.useEffect(updateOffset, [mousePos, isDragging, props.bottomCoordLimit, scroll]);

	React.useEffect(() => {
		return () => {
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('scroll', onScroll);
			document.removeEventListener('mouseup', onMouseUp);

			document.removeEventListener('touchmove', onTouchMove);
			if (scrollFncRef.current) document.removeEventListener('scroll', scrollFncRef.current);
			document.removeEventListener('touchend', onTouchEnd);
		};
	}, []);

	const changeTarget = (newTarget) => {
		if (newTarget != targetElement.current) {
			targetElement.current?.classList?.remove(DRAG_TARGET_CLASS);
			newTarget?.classList?.add(DRAG_TARGET_CLASS);
			targetElement.current = newTarget;

			props.onTargetChanged(newTarget as HTMLElement);
		}
	};

	const onMouseMove = (event: MouseEvent) => {
		//event.preventDefault();
		setMousePos({
			x: event.pageX - window.scrollX,
			y: event.pageY - window.scrollY,
		});
		changeTarget(event.target);
	};

	const onTouchMove = (event: TouchEvent) => {
		if (event.cancelable) {
			event.preventDefault();
		}
		const touch = event.touches?.[0];
		if (touch) {
			const x = touch.pageX - window.scrollX;
			const y = touch.pageY - window.scrollY;
			setMousePos({ x, y });
			const target = document.elementFromPoint(x, y);
			changeTarget(target);
		}
	};

	const onScroll = () => {
		setScroll(window.scrollY);
	};
	const onScrollTouch = (_e, keepScrollPos) => {
		if (keepScrollPos) {
			if (detectIncompatibleBrowsers()) {
				document.body.scrollTo(keepScrollPos?.x, keepScrollPos?.y);
			} else {
				window.scrollTo(keepScrollPos?.x, keepScrollPos?.y);
			}
		}
		onScroll();
	};

	const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
		if (!props.disabled) {
			document.addEventListener('touchend', onTouchEnd);
			//copy event, because for some reason it is null in the Timeout
			// (Object.assign does not work)
			const touch = event.touches?.[0];
			const firstTouch: React.Touch = {
				clientX: touch.clientX,
				clientY: touch.clientY,
				identifier: touch.identifier,
				pageX: touch.pageX,
				pageY: touch.pageY,
				screenX: touch.screenX,
				screenY: touch.screenY,
				target: document.elementFromPoint(touch.pageX, touch.pageY),
			};
			// scrollX is not page wise so use hack, to check if outerRef moved
			const scroll = [outerRef.current?.getBoundingClientRect()?.x, window.scrollY];
			holdTimer.current = setTimeout(() => {
				if (
					Math.abs(scroll[0] - outerRef.current?.getBoundingClientRect()?.x) + Math.abs(scroll[1] - window.scrollY) <
					HOLD_DISTANCE
				) {
					startDrag(firstTouch, true, {
						x: window.pageXOffset || document.documentElement.scrollLeft,
						y: window.pageYOffset || document.documentElement.scrollTop,
					});
				}
			}, TOUCH_HOLD_TIMEOUT);
		}
	};

	const onMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!props.disabled) {
			document.addEventListener('mouseup', onMouseUp);
			const eventCopy = Object.assign({}, event);
			holdTimer.current = setTimeout(() => {
				startDrag(eventCopy);
			}, MOUSE_HOLD_TIMEOUT);
		}
	};

	/**
	 * @param event
	 * @param isTouch is the event from a touchscreen device?
	 * @param keepScrollPos lock the scroll to this position
	 */
	const startDrag = (
		event: React.MouseEvent<HTMLDivElement> | React.Touch,
		isTouch = false,
		keepScrollPos?: { x: number; y: number },
	) => {
		if (props.onDragStart) props.onDragStart();
		if (isTouch) {
			document.addEventListener('touchmove', onTouchMove, {
				passive: false,
			});

			const scrollFnc = (e) => onScrollTouch(e, keepScrollPos);
			scrollFncRef.current = scrollFnc;
			document.addEventListener('scroll', scrollFnc);
		} else {
			document.addEventListener('mousemove', onMouseMove);

			document.addEventListener('scroll', onScroll);
		}
		if (innerRef?.current) {
			const innerDivRect = innerRef.current.getBoundingClientRect();

			setMouseOffset({
				x: event.pageX - innerDivRect.x - window.scrollX,
				y: event.pageY - innerDivRect.y - window.scrollY,
			});
			setMousePos({
				x: event.pageX - window.scrollX,
				y: event.pageY - window.scrollY,
			});
			setIsDragging(true);
		}
	};

	const onMouseUp = () => {
		document.removeEventListener('mousemove', onMouseMove);
		document.removeEventListener('scroll', onScroll);
		document.removeEventListener('mouseup', onMouseUp);
		dragEnd();
	};

	const onTouchEnd = () => {
		document.removeEventListener('touchmove', onTouchMove);
		document.removeEventListener('scroll', scrollFncRef.current);
		document.removeEventListener('touchend', onTouchEnd);
		dragEnd();
	};

	const dragEnd = () => {
		if (holdTimer.current) {
			clearTimeout(holdTimer.current);
		}
		if (targetElement.current) {
			targetElement.current?.classList?.remove(DRAG_TARGET_CLASS);
		}

		if (isDraggingRef.current) {
			props.onDragEnd(targetElement.current);
		}

		setIsDragging(false);
		setOffset({ x: 0, y: 0 });
	};

	return (
		<div
			ref={outerRef}
			style={
				isDragging
					? Object.assign({}, props.style, {
							pointerEvents: isDragging ? 'none' : 'auto',
							touchAction: isDragging ? 'none' : 'auto',
					  })
					: props.style
			}
			className="draggable-event-outer"
		>
			<div
				onClick={props.onClick}
				title={props.title}
				className={props.className + ' draggable-event-inner' + (isDragging ? ' dragging' : '')}
				onMouseDown={onMouseDown}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onTouchStart={onTouchStart}
				style={{
					left: offset?.x,
					top: offset?.y,
					pointerEvents: isDragging ? 'none' : 'auto',
					touchAction: isDragging ? 'none' : 'auto',
				}}
				ref={innerRef}
			>
				{props.children}
			</div>
		</div>
	);
};

export default DraggableEvent;
